import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'

import {
    Briefcase,
    Info,
    Plane,
    Sun
} from 'lucide-react'

import { cn } from '@/lib/utils'

// Caliandro et al. (2021, Nutrients): social jetlag is "the absolute difference
// between the midsleep point on free days (MSF) and the midsleep point on work
// (or school) days (MSW)", and "70% of students and workers experience at least
// one hour of SJL". The simple, uncorrected version is used here and the tool
// page says so.
const COMMON_THRESHOLD_MIN = 60
const HIGH_THRESHOLD_MIN = 120

// The timeline starts at 6pm so a normal night never wraps around the edge.
const TIMELINE_START = 18 * 60
const DAY = 1440
const TIMELINE_LABELS = ['6pm', '12am', '6am', '12pm', '6pm']

interface Schedule {
    asleep: string
    wake: string
}

const parseTime = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return hours * 60 + minutes
}

const formatClock = (total: number): string => {
    const wrapped = ((Math.round(total) % DAY) + DAY) % DAY
    const hours24 = Math.floor(wrapped / 60)
    const minutes = wrapped % 60
    const suffix = hours24 < 12 ? 'am' : 'pm'
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12

    return `${hours12}:${String(minutes).padStart(2, '0')} ${suffix}`
}

const formatDuration = (total: number): string => {
    const rounded = Math.round(total)
    const hours = Math.floor(rounded / 60)
    const minutes = rounded % 60
    if (hours === 0) return `${minutes} min`

    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

const describe = (schedule: Schedule) => {
    const asleep = parseTime(schedule.asleep)
    const wake = parseTime(schedule.wake)
    if (asleep === null || wake === null) return null

    const duration = (wake - asleep + DAY) % DAY
    if (duration === 0) return null

    return { asleep, duration, mid: (asleep + duration / 2) % DAY }
}

interface TimelineProps {
    label: string
    asleep: number
    duration: number
    mid: number
    tone: 'strong' | 'soft'
}

const Timeline: FC<TimelineProps> = ({ label, asleep, duration, mid, tone }): ReactNode => {
    const offset = ((asleep - TIMELINE_START + DAY) % DAY) / DAY
    const width = Math.min(duration / DAY, 1 - offset)
    const midOffset = ((mid - TIMELINE_START + DAY) % DAY) / DAY

    return (
        <div>
            <span className='block text-xs font-medium text-muted-foreground'>{label}</span>
            <div className='relative mt-1 h-7 rounded-lg bg-secondary'>
                <span
                    className={cn('absolute inset-y-0 rounded-lg', tone === 'strong' ? 'bg-primary' : 'bg-primary/45')}
                    style={{ left: `${offset * 100}%`, width: `${width * 100}%` }}
                />
                <span
                    className='absolute inset-y-[-4px] w-0.5 rounded-full bg-foreground'
                    style={{ left: `${midOffset * 100}%` }}
                />
            </div>
        </div>
    )
}

export const SocialJetlagCalculator: FC = (): ReactNode => {
    const [work, setWork] = useState<Schedule>({ asleep: '23:30', wake: '07:00' })
    const [free, setFree] = useState<Schedule>({ asleep: '01:00', wake: '09:30' })

    const workDay = describe(work)
    const freeDay = describe(free)

    // Signed gap between the two mid-sleep points, taking the shortest way round
    // the clock, so a 23:00 and a 01:00 midpoint are two hours apart, not 22.
    const shift =
        workDay && freeDay ? ((freeDay.mid - workDay.mid + DAY * 1.5) % DAY) - DAY / 2 : null
    const jetlag = shift === null ? null : Math.abs(shift)

    const verdict =
        jetlag === null
            ? null
            : jetlag < COMMON_THRESHOLD_MIN
                ? 'Under an hour. In one review, 70% of students and workers had at least an hour, so your week is steadier than most.'
                : jetlag < HIGH_THRESHOLD_MIN
                    ? 'Between one and two hours. That is common: in one review, 70% of students and workers had at least an hour.'
                    : 'Two hours or more. Your body clock is being asked to shift as if you changed time zones every week.'

    const inputs = [
        { key: 'work', title: 'On workdays', icon: Briefcase, value: work, set: setWork },
        { key: 'free', title: 'On free days', icon: Sun, value: free, set: setFree }
    ] as const

    return (
        <section
            aria-label='Social jetlag calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Plane className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Social jetlag calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Compare when you sleep on workdays and on free days to see how far your body clock shifts each week.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    {inputs.map(({ key, title, icon: Icon, value, set }) => (
                        <fieldset key={key} className='rounded-xl border border-border bg-background px-4 py-4'>
                            <legend className='flex items-center gap-2 px-1 text-sm font-semibold text-foreground'>
                                <Icon className='size-4 text-primary' />
                                {title}
                            </legend>
                            <div className='grid grid-cols-2 gap-3'>
                                <div>
                                    <label htmlFor={`jetlag-${key}-asleep`} className='block text-xs font-medium text-muted-foreground'>
                                        Fall asleep
                                    </label>
                                    <input
                                        id={`jetlag-${key}-asleep`}
                                        type='time'
                                        value={value.asleep}
                                        onChange={(event) => set({ ...value, asleep: event.target.value })}
                                        className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                    />
                                </div>
                                <div>
                                    <label htmlFor={`jetlag-${key}-wake`} className='block text-xs font-medium text-muted-foreground'>
                                        Wake up
                                    </label>
                                    <input
                                        id={`jetlag-${key}-wake`}
                                        type='time'
                                        value={value.wake}
                                        onChange={(event) => set({ ...value, wake: event.target.value })}
                                        className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                    />
                                </div>
                            </div>
                        </fieldset>
                    ))}
                </div>
                <span className='mt-2 block text-xs text-muted-foreground'>
                    Use the times you actually fall asleep and wake without trying, not the times you go to bed.
                </span>

                <div aria-live='polite' className='mt-6'>
                    {workDay === null || freeDay === null || jetlag === null || shift === null ? (
                        <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                            Enter a sleep and wake time for both kinds of day to see your result.
                        </span>
                    ) : (
                        <div>
                            <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                <span className='block text-sm font-medium text-foreground'>Your social jetlag</span>
                                <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                    {jetlag < 1 ? 'None' : formatDuration(jetlag)}
                                </span>
                                <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>{verdict}</span>
                            </div>

                            <div className='mt-5 flex flex-col gap-3'>
                                <Timeline
                                    label={`Workdays: ${formatDuration(workDay.duration)} asleep, midpoint ${formatClock(workDay.mid)}`}
                                    asleep={workDay.asleep}
                                    duration={workDay.duration}
                                    mid={workDay.mid}
                                    tone='strong'
                                />
                                <Timeline
                                    label={`Free days: ${formatDuration(freeDay.duration)} asleep, midpoint ${formatClock(freeDay.mid)}`}
                                    asleep={freeDay.asleep}
                                    duration={freeDay.duration}
                                    mid={freeDay.mid}
                                    tone='soft'
                                />
                                <div className='flex justify-between text-[0.7rem] text-muted-foreground' aria-hidden='true'>
                                    {TIMELINE_LABELS.map((item, index) => (
                                        <span key={`${item}-${index}`}>{item}</span>
                                    ))}
                                </div>
                            </div>

                            <span className='mt-4 block text-sm leading-relaxed text-muted-foreground'>
                                {Math.abs(shift) < 1
                                    ? 'Your sleep sits at the same point in the night on both kinds of day.'
                                    : `On free days, the middle of your sleep falls ${formatDuration(Math.abs(shift))} ${shift > 0 ? 'later' : 'earlier'} than on workdays.`}
                                {freeDay.duration - workDay.duration >= 30 &&
                                    ` You also sleep ${formatDuration(freeDay.duration - workDay.duration)} longer on free days, which this simple calculation does not correct for.`}
                            </span>
                        </div>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        An estimate based on your own typical times, not a sleep assessment. Nothing you enter
                        is stored. See{' '}
                        <Link href='/sleep/circadian-rhythm-101' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            what your circadian rhythm is
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default SocialJetlagCalculator
